<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-flask me-2"></i>Laboratory</h4>
        </div>
        <div class="d-flex gap-2">
          <button v-if="tab === 'requests'" class="hms-btn hms-btn-primary" @click="openNewRequest">
            <i class="mdi mdi-plus me-1"></i>New Request
          </button>
          <button v-if="tab === 'catalog'" class="hms-btn hms-btn-primary" @click="openAddTest">
            <i class="mdi mdi-plus me-1"></i>Add Test
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="setReqFilter('pending')" :class="{ selected: tab==='requests' && reqFilter==='pending' }">
          <div class="hms-stat-num hms-stat-amber">{{ statusCount('pending') }}</div>
          <div class="hms-stat-lbl">Pending</div>
        </div>
        <div class="hms-stat" @click="setReqFilter('sample_collected')" :class="{ selected: tab==='requests' && reqFilter==='sample_collected' }">
          <div class="hms-stat-num hms-stat-info">{{ statusCount('sample_collected') }}</div>
          <div class="hms-stat-lbl">Sample Collected</div>
        </div>
        <div class="hms-stat" @click="setReqFilter('processing')" :class="{ selected: tab==='requests' && reqFilter==='processing' }">
          <div class="hms-stat-num hms-stat-purple">{{ statusCount('processing') }}</div>
          <div class="hms-stat-lbl">Processing</div>
        </div>
        <div class="hms-stat" @click="setReqFilter('completed')" :class="{ selected: tab==='requests' && reqFilter==='completed' }">
          <div class="hms-stat-num hms-stat-green">{{ statusCount('completed') }}</div>
          <div class="hms-stat-lbl">Completed</div>
        </div>
        <div class="hms-stat" @click="setReqFilter('')" :class="{ selected: tab==='requests' && reqFilter==='' }">
          <div class="hms-stat-num">{{ requests.length }}</div>
          <div class="hms-stat-lbl">All Requests</div>
        </div>
        <div class="hms-stat" @click="tab='catalog'" :class="{ selected: tab==='catalog' }">
          <div class="hms-stat-num hms-stat-info">{{ tests.length }}</div>
          <div class="hms-stat-lbl">Tests in Catalog</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab === 'requests' }" @click="tab = 'requests'">
          <i class="mdi mdi-clipboard-text-clock me-1"></i>Requests
          <span class="hms-tab-count">{{ pendingWorkCount }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab === 'catalog' }" @click="tab = 'catalog'">
          <i class="mdi mdi-format-list-bulleted-type me-1"></i>Tests Catalog
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ REQUESTS TAB ============ -->
      <div v-else-if="tab === 'requests'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="reqSearch" placeholder="Search patient, request number..." />
            </div>
            <select class="hms-input hms-filter" v-model="reqFilter">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="sample_collected">Sample collected</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select class="hms-input hms-filter" v-model="reqPriorityFilter">
              <option value="">All priorities</option>
              <option value="stat">STAT</option>
              <option value="urgent">Urgent</option>
              <option value="routine">Routine</option>
            </select>
            <span class="hms-muted ms-auto">{{ filteredRequests.length }} requests</span>
          </div>

          <div v-if="filteredRequests.length" style="overflow-x:auto;">
            <table class="hms-table hms-sortable">
              <thead><tr>
                <th @click="toggleReqSort('request_number')" class="hms-th-sort">Request <i class="mdi" :class="sortIcon('request_number')"></i></th>
                <th @click="toggleReqSort('patient')"        class="hms-th-sort">Patient <i class="mdi" :class="sortIcon('patient')"></i></th>
                <th @click="toggleReqSort('requested_by')"   class="hms-th-sort">Requested By <i class="mdi" :class="sortIcon('requested_by')"></i></th>
                <th @click="toggleReqSort('date')"           class="hms-th-sort">Date <i class="mdi" :class="sortIcon('date')"></i></th>
                <th @click="toggleReqSort('priority')"       class="hms-th-sort">Priority <i class="mdi" :class="sortIcon('priority')"></i></th>
                <th @click="toggleReqSort('status')"         class="hms-th-sort">Status <i class="mdi" :class="sortIcon('status')"></i></th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="r in filteredRequests" :key="r.request_id">
                  <td><span class="hms-mono">{{ r.request_number }}</span></td>
                  <td>
                    <div class="hms-text fw-semibold">{{ r.patient_name }}</div>
                    <div class="hms-muted-sm">{{ r.patient_no }}</div>
                  </td>
                  <td>{{ r.requested_by_name || '—' }}</td>
                  <td>{{ formatDateTime(r.request_date) }}</td>
                  <td><span class="hms-badge" :class="priorityBadge(r.priority)">{{ formatPriority(r.priority) }}</span></td>
                  <td><span class="hms-badge" :class="reqStatusBadge(r.status)">{{ reqStatusLabel(r.status) }}</span></td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-primary me-1" @click="openWorkflow(r)" title="Open">
                      <i class="mdi mdi-arrow-right me-1"></i>Open
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-blue" @click="printLab(r.request_id)" title="Print results report">
                      <i class="mdi mdi-printer"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-flask-empty"></i>No lab requests match.</div>
        </div>
      </div>

      <!-- ============ CATALOG TAB ============ -->
      <div v-else-if="tab === 'catalog'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="testSearch" placeholder="Search test name, code, category..." />
            </div>
            <span class="hms-muted ms-auto">{{ filteredTests.length }} tests</span>
          </div>

          <div v-if="filteredTests.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Test</th>
                <th>Code</th>
                <th>Category</th>
                <th>Sample</th>
                <th>TAT</th>
                <th>Price</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="t in filteredTests" :key="t.test_id">
                  <td>
                    <div class="hms-text fw-semibold">{{ t.test_name }}</div>
                    <div class="hms-muted-sm" v-if="t.requires_fasting == 1"><i class="mdi mdi-food-off-outline me-1"></i>Fasting required</div>
                  </td>
                  <td><span class="hms-mono">{{ t.test_code || '—' }}</span></td>
                  <td>{{ t.category || '—' }}</td>
                  <td>{{ t.sample_type || '—' }}</td>
                  <td>{{ t.turnaround_hours }}h</td>
                  <td>KES {{ formatMoney(t.price) }}</td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openEditTest(t)" title="Edit">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-format-list-bulleted-type"></i>No tests in catalog.</div>
        </div>
      </div>

      <!-- ============ WORKFLOW MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showWorkflow" @click.self="closeWorkflow">
        <div class="hms-modal" v-if="reqDetail || workflowLoading">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-clipboard-text-clock me-2"></i>{{ reqDetail ? reqDetail.request_number : 'Lab Request' }}</span>
            <button class="hms-modal-close" @click="closeWorkflow">&times;</button>
          </div>

          <div v-if="workflowLoading" class="hms-spinner py-3"><div class="spinner-border"></div></div>

          <div v-else-if="reqDetail">
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Patient</span><span>{{ reqDetail.patient_name }} ({{ reqDetail.patient_no }})</span></div>
              <div><span class="hms-kv-key">Requested By</span><span>{{ reqDetail.requested_by_name || '—' }}</span></div>
              <div><span class="hms-kv-key">Date</span><span>{{ formatDateTime(reqDetail.request_date) }}</span></div>
              <div><span class="hms-kv-key">Priority</span><span><span class="hms-badge" :class="priorityBadge(reqDetail.priority)">{{ formatPriority(reqDetail.priority) }}</span></span></div>
              <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="reqStatusBadge(reqDetail.status)">{{ reqStatusLabel(reqDetail.status) }}</span></span></div>
              <div v-if="reqDetail.clinical_notes"><span class="hms-kv-key">Clinical Notes</span><span>{{ reqDetail.clinical_notes }}</span></div>
            </div>

            <div class="hms-section-label">TESTS</div>
            <div v-for="line in reqItems" :key="line.item_id" class="hms-lab-line">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div class="hms-text fw-semibold">{{ line.test_name }}</div>
                  <div class="hms-muted-sm" v-if="line.sample_type">Sample: {{ line.sample_type }}</div>
                </div>
                <span class="hms-badge" :class="itemStatusBadge(line.status)">{{ itemStatusLabel(line.status) }}</span>
              </div>

              <!-- PENDING → Collect Sample -->
              <div v-if="line.status === 'pending'" class="hms-lab-action">
                <button class="hms-btn hms-btn-blue" @click="collectSample(line)" :disabled="processing">
                  <i class="mdi mdi-test-tube me-1"></i>Collect Sample
                </button>
              </div>

              <!-- SAMPLE_COLLECTED / PROCESSING → Enter Result -->
              <div v-else-if="line.status === 'sample_collected' || line.status === 'processing'" class="hms-lab-form">
                <div class="hms-muted-sm mb-2" v-if="line.collected_by_name">
                  Sample collected by {{ line.collected_by_name }} at {{ formatDateTime(line.sample_collected_at) }}
                </div>
                <div class="row g-2">
                  <div class="col-md-6"><label class="hms-label">Result Value *</label>
                    <input class="hms-input" v-model="line.result_value" placeholder="e.g. 12.4" />
                  </div>
                  <div class="col-md-3"><label class="hms-label">Unit</label>
                    <input class="hms-input" v-model="line.result_unit" :placeholder="line.test_unit || ''" />
                  </div>
                  <div class="col-md-3"><label class="hms-label">Flag</label>
                    <select class="hms-input" v-model="line.result_flag">
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="low">Low</option>
                      <option value="critical_high">Critical High</option>
                      <option value="critical_low">Critical Low</option>
                      <option value="abnormal">Abnormal</option>
                    </select>
                  </div>
                  <div class="col-md-12"><label class="hms-label">Normal Range</label>
                    <input class="hms-input" v-model="line.normal_range" :placeholder="line.normal_range_male || line.normal_range_female || ''" />
                  </div>
                  <div class="col-md-12"><label class="hms-label">Notes</label>
                    <textarea class="hms-input" rows="2" v-model="line.result_notes" placeholder="Optional comments"></textarea>
                  </div>
                </div>
                <button class="hms-btn hms-btn-primary mt-2" @click="enterResult(line)" :disabled="processing || !line.result_value">
                  <i class="mdi mdi-pencil me-1"></i>Save Result
                </button>
              </div>

              <!-- RESULTED → Verify -->
              <div v-else-if="line.status === 'resulted'">
                <div class="hms-result-display mb-2">
                  <div><strong>Result:</strong> {{ line.result_value }} {{ line.result_unit || '' }}
                    <span v-if="line.result_flag" class="hms-badge ms-2" :class="resultFlagBadge(line.result_flag)">{{ formatFlag(line.result_flag) }}</span>
                  </div>
                  <div v-if="line.normal_range" class="hms-muted-sm">Normal range: {{ line.normal_range }}</div>
                  <div v-if="line.result_notes" class="hms-muted-sm mt-1">{{ line.result_notes }}</div>
                  <div class="hms-muted-sm mt-1" v-if="line.result_by_name">
                    Entered by {{ line.result_by_name }} at {{ formatDateTime(line.result_entered_at) }}
                  </div>
                </div>
                <button class="hms-btn hms-btn-primary" @click="verifyResult(line)" :disabled="processing">
                  <i class="mdi mdi-check-decagram me-1"></i>Verify Result
                </button>
              </div>

              <!-- VERIFIED → Read-only -->
              <div v-else-if="line.status === 'verified'" class="hms-result-display">
                <div><strong>Result:</strong> {{ line.result_value }} {{ line.result_unit || '' }}
                  <span v-if="line.result_flag" class="hms-badge ms-2" :class="resultFlagBadge(line.result_flag)">{{ formatFlag(line.result_flag) }}</span>
                </div>
                <div v-if="line.normal_range" class="hms-muted-sm">Normal range: {{ line.normal_range }}</div>
                <div v-if="line.result_notes" class="hms-muted-sm mt-1">{{ line.result_notes }}</div>
                <div class="hms-muted-sm mt-2 hms-text-success">
                  <i class="mdi mdi-check-decagram me-1"></i>Verified by {{ line.verified_by_name || 'lab' }} at {{ formatDateTime(line.verified_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ NEW REQUEST MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showNewRequest" @click.self="showNewRequest=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus-circle me-2"></i>New Lab Request</span>
            <button class="hms-modal-close" @click="showNewRequest=false">&times;</button>
          </div>
          <div v-if="newRequestError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ newRequestError }}</div>

          <!-- Patient picker -->
          <div class="mb-3">
            <label class="hms-label">Patient *</label>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="patientSearch" placeholder="Search name, patient no, phone, ID..." @input="onPatientSearch" />
            </div>
            <div v-if="patientResults.length" class="hms-suggest">
              <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.phone || '—' }} · {{ pp.gender || '—' }}</div>
              </div>
            </div>
            <div v-if="newRequest.patient_id" class="hms-picked mt-2">
              <i class="mdi mdi-check-circle me-1"></i>
              {{ newRequest.patient_name }} <span class="hms-muted-sm ms-1">({{ newRequest.patient_no }})</span>
              <button class="hms-modal-close ms-auto" @click="clearPatient" title="Clear">&times;</button>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Priority</label>
              <select class="hms-input" v-model="newRequest.priority">
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="stat">STAT</option>
              </select>
            </div>
          </div>

          <div class="mb-3"><label class="hms-label">Clinical Notes</label>
            <textarea class="hms-input" rows="2" v-model="newRequest.clinical_notes" placeholder="Indication, suspected diagnosis..."></textarea>
          </div>

          <div class="hms-section-label">SELECT TESTS *</div>
          <div class="hms-search-wrap mb-2">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="catalogPickSearch" placeholder="Filter tests..." />
          </div>
          <div class="hms-test-picker">
            <label v-for="t in catalogPickFiltered" :key="t.test_id" class="hms-test-pick">
              <input type="checkbox" :value="t.test_id" v-model="newRequest.test_ids" />
              <div class="flex-grow-1">
                <div class="hms-text">{{ t.test_name }}</div>
                <div class="hms-muted-sm">{{ t.category || '' }} · TAT {{ t.turnaround_hours }}h · KES {{ formatMoney(t.price) }}</div>
              </div>
            </label>
          </div>
          <div class="hms-muted-sm mt-2">{{ newRequest.test_ids.length }} test(s) selected</div>

          <button class="hms-btn hms-btn-primary w-100 mt-3" @click="submitNewRequest" :disabled="processing || !newRequest.patient_id || !newRequest.test_ids.length">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Creating...' : 'Create Lab Request' }}
          </button>
        </div>
      </div>

      <!-- ============ ADD/EDIT TEST MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showTestForm" @click.self="showTestForm=false">
        <div class="hms-modal" style="max-width:600px;">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="editingTest ? 'mdi-pencil' : 'mdi-plus-circle'"></i> {{ editingTest ? 'Edit Test' : 'Add Test' }}</span>
            <button class="hms-modal-close" @click="showTestForm=false">&times;</button>
          </div>
          <div v-if="testError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ testError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-8"><label class="hms-label">Test Name *</label>
              <input class="hms-input" v-model="testForm.test_name" placeholder="e.g. Complete Blood Count" />
            </div>
            <div class="col-md-4"><label class="hms-label">Code</label>
              <input class="hms-input" v-model="testForm.test_code" placeholder="CBC" />
            </div>
            <div class="col-md-6"><label class="hms-label">Category</label>
              <input class="hms-input" v-model="testForm.category" placeholder="Haematology" />
            </div>
            <div class="col-md-6"><label class="hms-label">Sample Type</label>
              <input class="hms-input" v-model="testForm.sample_type" placeholder="Whole blood EDTA" />
            </div>
            <div class="col-md-4"><label class="hms-label">Unit</label>
              <input class="hms-input" v-model="testForm.unit" placeholder="g/dL" />
            </div>
            <div class="col-md-4"><label class="hms-label">TAT (hours)</label>
              <input type="number" class="hms-input" v-model.number="testForm.turnaround_hours" min="0" />
            </div>
            <div class="col-md-4"><label class="hms-label">Price (KES)</label>
              <input type="number" class="hms-input" v-model.number="testForm.price" min="0" step="0.01" />
            </div>
            <div class="col-md-4"><label class="hms-label">Normal (Male)</label>
              <input class="hms-input" v-model="testForm.normal_range_male" placeholder="13.5-17.5" />
            </div>
            <div class="col-md-4"><label class="hms-label">Normal (Female)</label>
              <input class="hms-input" v-model="testForm.normal_range_female" placeholder="12.0-16.0" />
            </div>
            <div class="col-md-4"><label class="hms-label">Normal (Child)</label>
              <input class="hms-input" v-model="testForm.normal_range_child" placeholder="11.0-14.0" />
            </div>
            <div class="col-md-12"><label class="hms-label">Result Options (comma-separated)</label>
              <input class="hms-input" v-model="testForm.result_options" placeholder="e.g. Positive, Negative, Indeterminate" />
              <div class="hms-muted-sm mt-1">If filled, the lab tech picks the result from this list instead of typing. Leave blank for numeric / free-text results.</div>
            </div>
          </div>
          <div class="d-flex flex-wrap gap-3 mb-3">
            <label class="hms-check"><input type="checkbox" v-model="testForm.requires_fasting" :true-value="1" :false-value="0" /> Requires fasting</label>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveTest" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editingTest ? 'Save Changes' : 'Add Test') }}
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-text-success { color: var(--hms-success); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; color: var(--hms-h); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 150px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-amber { color: #f1b44c; }
.hms-stat-info { color: var(--hms-primary); }
.hms-stat-purple { color: #9b59b6; }
.hms-stat-green { color: var(--hms-success); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tab-count { background: var(--hms-primary); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 10px; margin-left: 8px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; margin-top: 8px; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; }
.hms-check input { accent-color: var(--hms-primary); }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 140px; font-weight: 600; }

.hms-lab-line { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 14px; margin-bottom: 12px; }
.hms-lab-form { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; padding: 12px; margin-top: 8px; }
.hms-lab-action { margin-top: 8px; }
.hms-result-display { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; padding: 10px 14px; margin-top: 4px; font-size: 14px; }

.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 220px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }

.hms-test-picker { max-height: 280px; overflow-y: auto; border: 1px solid var(--hms-br); border-radius: 8px; }
.hms-test-pick { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-bottom: 1px solid var(--hms-br); cursor: pointer; }
.hms-test-pick:last-child { border-bottom: none; }
.hms-test-pick:hover { background: var(--hms-primary-lt); }
.hms-test-pick input { accent-color: var(--hms-primary); }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

@media (max-width: 768px) {
  .hms-stat-row .hms-stat { min-width: calc(50% - 6px); }
  .hms-modal { padding: 18px; }
}

/* Sortable table headers */
.hms-sortable .hms-th-sort { cursor: pointer; user-select: none; white-space: nowrap; }
.hms-sortable .hms-th-sort:hover { background: var(--hms-bg3); color: var(--hms-primary); }
.hms-sortable .hms-th-sort .mdi { font-size: 14px; margin-left: 3px; opacity: .8; vertical-align: middle; }
.hms-sortable .hms-th-sort .hms-sort-idle { opacity: .35; }
.hms-sortable .hms-th-sort .hms-sort-active { color: var(--hms-primary); opacity: 1; }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "LabManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "requests",

      requests: [],
      reqSearch: "",
      reqFilter: "",
      reqPriorityFilter: "",
      reqSortKey: 'date', reqSortDir: 'desc',

      tests: [],
      testSearch: "",

      // Workflow modal
      showWorkflow: false,
      workflowLoading: false,
      reqDetail: null,
      reqItems: [],

      // New request modal
      showNewRequest: false,
      newRequestError: "",
      newRequest: this.emptyNewRequest(),
      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],
      catalogPickSearch: "",

      // Add/edit test
      showTestForm: false,
      editingTest: false,
      testError: "",
      testForm: this.emptyTestForm(),

      toastMsg: "",
      toastError: false,
      toastTimer: null,

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    pendingWorkCount() {
      return this.requests.filter(r => ['pending','sample_collected','processing'].indexOf(r.status) >= 0).length;
    },
    filteredRequests() {
      var list = this.requests;
      if (this.reqFilter) list = list.filter(r => r.status === this.reqFilter);
      if (this.reqPriorityFilter) list = list.filter(r => r.priority === this.reqPriorityFilter);
      var q = this.reqSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(r => ((r.patient_name || "") + " " + (r.request_number || "") + " " + (r.patient_no || "")).toLowerCase().indexOf(q) >= 0);
      }
      // Sortable columns — click header cycles asc → desc → none.
      if (this.reqSortKey) {
        var key = this.reqSortKey, dir = this.reqSortDir;
        var priorityRank = { emergency: 0, stat: 0, urgent: 1, routine: 2, elderly_disabled: 1, normal: 2 };
        var statusRank   = { pending: 0, sample_collected: 1, processing: 2, completed: 3, cancelled: 4 };
        var sorted = list.slice().sort(function (a, b) {
          var av, bv;
          switch (key) {
            case 'request_number': av = (a.request_number || '').toLowerCase(); bv = (b.request_number || '').toLowerCase(); break;
            case 'patient':        av = (a.patient_name   || '').toLowerCase(); bv = (b.patient_name   || '').toLowerCase(); break;
            case 'requested_by':   av = (a.requested_by_name || '').toLowerCase(); bv = (b.requested_by_name || '').toLowerCase(); break;
            case 'date':           av = a.request_date ? new Date(a.request_date).getTime() : 0; bv = b.request_date ? new Date(b.request_date).getTime() : 0; break;
            case 'priority':       av = priorityRank[(a.priority || '').toLowerCase()] ?? 99; bv = priorityRank[(b.priority || '').toLowerCase()] ?? 99; break;
            case 'status':         av = statusRank[(a.status || '').toLowerCase()] ?? 99;   bv = statusRank[(b.status || '').toLowerCase()] ?? 99;   break;
            default: av = 0; bv = 0;
          }
          if (av < bv) return dir === 'desc' ? 1 : -1;
          if (av > bv) return dir === 'desc' ? -1 : 1;
          return 0;
        });
        return sorted;
      }
      return list;
    },
    filteredTests() {
      var q = this.testSearch.trim().toLowerCase();
      if (!q) return this.tests;
      return this.tests.filter(t => ((t.test_name || "") + " " + (t.test_code || "") + " " + (t.category || "")).toLowerCase().indexOf(q) >= 0);
    },
    catalogPickFiltered() {
      var q = this.catalogPickSearch.trim().toLowerCase();
      if (!q) return this.tests;
      return this.tests.filter(t => ((t.test_name || "") + " " + (t.test_code || "") + " " + (t.category || "")).toLowerCase().indexOf(q) >= 0);
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    toggleReqSort(key) {
      // Click header: cycle asc → desc → clear.
      if (this.reqSortKey !== key) { this.reqSortKey = key; this.reqSortDir = 'asc'; return; }
      if (this.reqSortDir === 'asc') { this.reqSortDir = 'desc'; return; }
      this.reqSortKey = ''; this.reqSortDir = 'asc';
    },
    sortIcon(key) {
      if (this.reqSortKey !== key) return 'mdi-unfold-more-horizontal hms-sort-idle';
      return this.reqSortDir === 'asc' ? 'mdi-arrow-up hms-sort-active' : 'mdi-arrow-down hms-sort-active';
    },
    printLab(id) { window.open(this.$router.resolve({ name: 'print', params: { type: 'lab', id }, query: { auto: '1' } }).href, '_blank'); },
    emptyNewRequest() {
      return {
        patient_id: null, patient_name: "", patient_no: "",
        priority: "routine", clinical_notes: "", test_ids: []
      };
    },
    emptyTestForm() {
      return {
        test_id: null, test_name: "", test_code: "", category: "", sample_type: "",
        normal_range_male: "", normal_range_female: "", normal_range_child: "",
        unit: "", result_options: "", turnaround_hours: 24, price: 0, requires_fasting: 0
      };
    },
    statusCount(s) { return this.requests.filter(r => r.status === s).length; },
    formatDateTime(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch(e) { return d; }
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "0.00";
      return Number(v).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatPriority(p) { var m = { routine:'Routine', urgent:'Urgent', stat:'STAT' }; return m[p] || p; },
    formatFlag(f) {
      var m = { normal:'Normal', high:'High', low:'Low', critical_high:'Critical High', critical_low:'Critical Low', abnormal:'Abnormal' };
      return m[f] || f;
    },
    priorityBadge(p) { return p === 'stat' ? 'hms-badge-danger' : (p === 'urgent' ? 'hms-badge-amber' : 'hms-badge-neutral'); },
    reqStatusBadge(s) {
      var m = { pending:'hms-badge-warning', sample_collected:'hms-badge-info', processing:'hms-badge-purple', completed:'hms-badge-success', cancelled:'hms-badge-danger' };
      return m[s] || 'hms-badge-neutral';
    },
    reqStatusLabel(s) {
      var m = { pending:'Pending', sample_collected:'Sample Collected', processing:'Processing', completed:'Completed', cancelled:'Cancelled' };
      return m[s] || s;
    },
    itemStatusBadge(s) {
      var m = { pending:'hms-badge-warning', sample_collected:'hms-badge-info', processing:'hms-badge-purple', resulted:'hms-badge-amber', verified:'hms-badge-success', cancelled:'hms-badge-danger' };
      return m[s] || 'hms-badge-neutral';
    },
    itemStatusLabel(s) {
      var m = { pending:'Pending', sample_collected:'Sample Collected', processing:'Processing', resulted:'Resulted', verified:'Verified', cancelled:'Cancelled' };
      return m[s] || s;
    },
    resultFlagBadge(f) {
      switch (f) {
        case 'critical_high':
        case 'critical_low':
        case 'abnormal':       return 'hms-badge-danger';
        case 'high':
        case 'low':            return 'hms-badge-warning';
        case 'normal':         return 'hms-badge-success';
        default:               return 'hms-badge-neutral';
      }
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2800);
    },
    setReqFilter(f) { this.tab = "requests"; this.reqFilter = f; },

    async loadAll() {
      this.loading = true;
      try { await Promise.all([this.loadRequests(), this.loadTests()]); }
      catch (e) { console.error(e); }
      this.loading = false;
    },
    async loadRequests() {
      try {
        var r = await executeJson({ function: "getLabRequests", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.requests = (r.data.data && r.data.data.LabRequests) || [];
      } catch (e) { console.error(e); }
    },
    async loadTests() {
      try {
        var r = await executeJson({ function: "getLabTests" }, hms);
        if (r.data && r.data.success) this.tests = (r.data.data && r.data.data.LabTests) || [];
      } catch (e) { console.error(e); }
    },

    // ===== WORKFLOW =====
    async openWorkflow(req) {
      this.showWorkflow = true;
      this.workflowLoading = true;
      this.reqDetail = null;
      this.reqItems = [];
      try {
        var r = await executeJson({ function: "getLabRequestDetail", request_id: req.request_id }, hms);
        if (r.data && r.data.success) {
          this.reqDetail = r.data.data.Request;
          this.reqItems = (r.data.data.Items || []).map(line => {
            // Hydrate fields used by the result form
            line.result_value = line.result_value || "";
            line.result_unit  = line.result_unit  || "";
            line.normal_range = line.normal_range || "";
            line.result_flag  = line.result_flag  || "normal";
            line.result_notes = line.result_notes || "";
            return line;
          });
        }
      } catch (e) { this.showToast("Failed to load request", true); }
      this.workflowLoading = false;
    },
    closeWorkflow() {
      this.showWorkflow = false;
      this.reqDetail = null;
      this.reqItems = [];
    },
    async refreshDetail() {
      if (!this.reqDetail) return;
      var rid = this.reqDetail.request_id;
      try {
        var r = await executeJson({ function: "getLabRequestDetail", request_id: rid }, hms);
        if (r.data && r.data.success) {
          this.reqDetail = r.data.data.Request;
          this.reqItems  = r.data.data.Items || [];
        }
      } catch (e) { /* keep state */ }
    },

    async collectSample(line) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "collectSample", item_id: line.item_id, request_id: line.request_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Sample collected");
          await Promise.all([this.refreshDetail(), this.loadRequests()]);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async enterResult(line) {
      if (!line.result_value) { this.showToast("Result value is required", true); return; }
      this.processing = true;
      try {
        var r = await executeJson({
          function:     "enterLabResult",
          item_id:      line.item_id,
          request_id:   line.request_id,
          result_value: line.result_value,
          result_unit:  line.result_unit,
          normal_range: line.normal_range,
          result_flag:  line.result_flag,
          result_notes: line.result_notes
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Result entered");
          await Promise.all([this.refreshDetail(), this.loadRequests()]);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async verifyResult(line) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "verifyLabResult", item_id: line.item_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Result verified");
          await Promise.all([this.refreshDetail(), this.loadRequests()]);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    // ===== NEW REQUEST =====
    openNewRequest() {
      this.newRequest = this.emptyNewRequest();
      this.newRequestError = "";
      this.patientSearch = "";
      this.patientResults = [];
      this.catalogPickSearch = "";
      this.showNewRequest = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch (e) { /* silent */ }
      }, 300);
    },
    pickPatient(p) {
      this.newRequest.patient_id   = p.patient_id;
      this.newRequest.patient_name = (p.first_name + ' ' + p.last_name).trim();
      this.newRequest.patient_no   = p.patient_no;
      this.patientResults = [];
      this.patientSearch  = "";
    },
    clearPatient() {
      this.newRequest.patient_id   = null;
      this.newRequest.patient_name = "";
      this.newRequest.patient_no   = "";
    },
    async submitNewRequest() {
      this.newRequestError = "";
      if (!this.newRequest.patient_id) { this.newRequestError = "Pick a patient."; return; }
      if (!this.newRequest.test_ids.length) { this.newRequestError = "Select at least one test."; return; }

      var pickedTests = this.tests.filter(t => this.newRequest.test_ids.indexOf(t.test_id) >= 0)
        .map(t => ({
          test_id:     t.test_id,
          test_name:   t.test_name,
          sample_type: t.sample_type,
          price:       t.price
        }));

      this.processing = true;
      try {
        var r = await executeJson({
          function:        "requestLabTest",
          patient_id:      this.newRequest.patient_id,
          priority:        this.newRequest.priority,
          clinical_notes:  this.newRequest.clinical_notes,
          tests:           pickedTests
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Lab request created");
          this.showNewRequest = false;
          await this.loadRequests();
        } else { this.newRequestError = r.data.message || "Failed."; }
      } catch (e) { this.newRequestError = "Connection error."; }
      this.processing = false;
    },

    // ===== TEST CATALOG =====
    openAddTest() {
      this.editingTest = false;
      this.testForm    = this.emptyTestForm();
      this.testError   = "";
      this.showTestForm = true;
    },
    openEditTest(t) {
      this.editingTest = true;
      this.testForm = Object.assign(this.emptyTestForm(), {
        test_id: t.test_id,
        test_name: t.test_name || "",
        test_code: t.test_code || "",
        category: t.category || "",
        sample_type: t.sample_type || "",
        normal_range_male: t.normal_range_male || "",
        normal_range_female: t.normal_range_female || "",
        normal_range_child: t.normal_range_child || "",
        unit: t.unit || "",
        result_options: t.result_options || "",
        turnaround_hours: Number(t.turnaround_hours) || 24,
        price: Number(t.price) || 0,
        requires_fasting: Number(t.requires_fasting) || 0
      });
      this.testError = "";
      this.showTestForm = true;
    },
    async saveTest() {
      this.testError = "";
      if (!this.testForm.test_name) { this.testError = "Test name is required."; return; }
      this.processing = true;
      try {
        // updateLabTest handler doesn't exist yet — for now, both create and edit go through createLabTest
        // (in practice, edit would soft-update an existing row; we keep this scoped to add).
        var r = await executeJson(Object.assign({ function: "createLabTest" }, this.testForm), hms);
        if (r.data && r.data.success) {
          this.showToast(this.editingTest ? "Test saved (as new)" : "Test added");
          this.showTestForm = false;
          await this.loadTests();
        } else { this.testError = r.data.message || "Failed."; }
      } catch (e) { this.testError = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
